import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { useAuth } from '../../Providers/AuthProvider';
import {
    LookupVoucherBL,
    RedeemPrizeBL,
    rouletteAudioEngine,
    formatEventDate,
    parseVoucherCode
} from '../../../utilities/BL/PersonalRoulette';

/**
 * MODAL DE CANJE DUAL EN SITIO: RULETA BAJA CUP (PersonalRouletteRedeemModal) [HU-05]
 * Soporta escaneo en vivo con cámara QR, selección de cámaras, canje rápido automático,
 * búsqueda manual por folio/teléfono/correo y tarjeta de validación Pre-check con sintetizador de audio.
 */
export const PersonalRouletteRedeemModal = ({
    isOpen,
    onClose,
    roomId = null,
    initialCode = '',
    onRedeemSuccess = null
}) => {
    const { user } = useAuth();
    const operatorName = user?.username || 'Staff';

    // 1. Estado de Pestañas (Búsqueda manual por defecto)
    const [activeTab, setActiveTab] = useState('manual'); // 'scanner' | 'manual'

    // 2. Estado de Cámara y Escáner QR
    const [cameras, setCameras] = useState([]);
    const [selectedCameraId, setSelectedCameraId] = useState('');
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [cameraError, setCameraError] = useState('');
    const [isQuickAutoRedeem, setIsQuickAutoRedeem] = useState(false);

    // 3. Estado de Búsqueda Manual
    const [manualInput, setManualInput] = useState('');
    const [isLoadingLookup, setIsLoadingLookup] = useState(false);
    const [notesInput, setNotesInput] = useState('');

    // 4. Estado de Resultado / Pre-check Card
    // null | 'WINNER_PENDING' | 'ALREADY_REDEEMED' | 'NOT_A_WINNER' | 'NOT_FOUND' | 'REDEEMED_SUCCESS' | 'ERROR'
    const [resultStatus, setResultStatus] = useState(null);
    const [resultData, setResultData] = useState(null);
    const [resultMessage, setResultMessage] = useState('');
    const [isRedeeming, setIsRedeeming] = useState(false);

    // Referencias internas para el ciclo de vida de la cámara
    const scannerRef = useRef(null);
    const isScanningRef = useRef(false);
    const isProcessingScanRef = useRef(false);
    const qrRegionId = 'personal-roulette-qr-reader';

    // =========================================================================
    // DETENCIÓN SEGURA DEL ESCÁNER DE CÁMARA
    // =========================================================================
    const stopCameraScanner = useCallback(async () => {
        if (scannerRef.current && isScanningRef.current) {
            try {
                isScanningRef.current = false;
                await scannerRef.current.stop();
            } catch (err) {
                // Ignorar si ya estaba detenido
            }
        }
        setIsCameraActive(false);
    }, []);

    // =========================================================================
    // INICIALIZACIÓN Y ARRANQUE DEL ESCÁNER DE CÁMARA
    // =========================================================================
    const startCameraScanner = useCallback(async (cameraIdToUse = null) => {
        setCameraError('');
        try {
            await stopCameraScanner();

            // Asegurar que el elemento HTML existe en el DOM
            const container = document.getElementById(qrRegionId);
            if (!container) return;

            if (!scannerRef.current) {
                scannerRef.current = new Html5Qrcode(qrRegionId, { verbose: false });
            }

            const deviceId = cameraIdToUse || selectedCameraId;
            const cameraConfig = deviceId
                ? { deviceId: { exact: deviceId } }
                : { facingMode: 'environment' };

            const qrConfig = {
                fps: 12,
                qrbox: (viewfinderWidth, viewfinderHeight) => {
                    const minDim = Math.min(viewfinderWidth, viewfinderHeight);
                    return {
                        width: Math.floor(minDim * 0.72),
                        height: Math.floor(minDim * 0.72)
                    };
                },
                aspectRatio: 1.0
            };

            await scannerRef.current.start(
                cameraConfig,
                qrConfig,
                async (decodedText) => {
                    // Evitar múltiples lecturas concurrentes del mismo fotograma
                    if (isProcessingScanRef.current) return;
                    isProcessingScanRef.current = true;

                    const cleanScannedCode = parseVoucherCode(decodedText);
                    if (!cleanScannedCode) {
                        isProcessingScanRef.current = false;
                        return;
                    }

                    // Si está activo el Canje Rápido Automático
                    if (isQuickAutoRedeem) {
                        await handleExecuteRedeem({
                            passCode: cleanScannedCode,
                            redeemChannel: 'QUICK_AUTO',
                            autoLookup: true
                        });
                    } else {
                        // Modo Pre-check: primero validar y mostrar tarjeta
                        await handleLookupParticipant(cleanScannedCode, 'QR_SCAN');
                    }

                    // Pausar temporalmente el escaneo para no re-procesar de inmediato
                    setTimeout(() => {
                        isProcessingScanRef.current = false;
                    }, 1800);
                },
                () => {
                    // Frame scan parse miss (ignorar de forma silenciosa)
                }
            );

            isScanningRef.current = true;
            setIsCameraActive(true);
        } catch (err) {
            console.error('Error al iniciar escáner de cámara:', err);
            isScanningRef.current = false;
            setIsCameraActive(false);
            setCameraError(
                err?.name === 'NotAllowedError' || err?.message?.includes('Permission')
                    ? 'Permiso de cámara denegado. Concede permisos en tu navegador.'
                    : 'No se pudo acceder a la cámara seleccionada. Intenta con otra cámara.'
            );
        }
    }, [selectedCameraId, isQuickAutoRedeem, stopCameraScanner]);

    // =========================================================================
    // OBTENCIÓN DE CÁMARAS DISPONIBLES (Deshabilitado en modo manual silencioso)
    // =========================================================================
    useEffect(() => {
        // En modo manual no se listan ni inicializan cámaras para evitar activación innecesaria del hardware
        if (!isOpen || activeTab !== 'scanner') return;

        let isMounted = true;
        if (typeof Html5Qrcode !== 'undefined' && Html5Qrcode.getCameras) {
            Html5Qrcode.getCameras()
                .then((devices) => {
                    if (isMounted && devices && devices.length > 0) {
                        setCameras(devices);
                        const backCam = devices.find(d =>
                            d.label.toLowerCase().includes('back') ||
                            d.label.toLowerCase().includes('trasera') ||
                            d.label.toLowerCase().includes('rear') ||
                            d.label.toLowerCase().includes('environment')
                        );
                        const defaultId = backCam ? backCam.id : devices[0].id;
                        setSelectedCameraId(defaultId);
                    }
                })
                .catch((err) => {
                    console.warn('No se pudieron listar las cámaras:', err);
                });
        }

        return () => {
            isMounted = false;
        };
    }, [isOpen, activeTab]);

    // Manejar apertura/cierre y cambio de pestaña
    useEffect(() => {
        if (!isOpen) {
            stopCameraScanner();
            setResultStatus(null);
            setResultData(null);
            setResultMessage('');
            setManualInput('');
            setNotesInput('');
            return;
        }

        // Si viene un código inicial precargado (ej. desde el botón de la tabla)
        if (initialCode) {
            setActiveTab('manual');
            setManualInput(initialCode);
            handleLookupParticipant(initialCode, 'MANUAL_FOLIO');
        } else if (activeTab === 'scanner' && !resultStatus) {
            // Arrancar cámara tras montar DOM
            const timer = setTimeout(() => {
                startCameraScanner();
            }, 300);
            return () => clearTimeout(timer);
        } else if (activeTab === 'manual') {
            stopCameraScanner();
        }
    }, [isOpen, activeTab]);

    // Limpieza al desmontar
    useEffect(() => {
        return () => {
            stopCameraScanner();
        };
    }, [stopCameraScanner]);

    // =========================================================================
    // LÓGICA DE VALIDACIÓN Y BÚSQUEDA (LOOKUP)
    // =========================================================================
    const handleLookupParticipant = async (codeOrPhone, channel = 'MANUAL_FOLIO') => {
        if (!codeOrPhone || !codeOrPhone.trim()) return;

        setIsLoadingLookup(true);
        setResultStatus(null);
        setResultData(null);
        setResultMessage('');

        try {
            const res = await LookupVoucherBL(roomId, codeOrPhone.trim());
            setResultData(res.data?.participant || res.data || null);
            setResultMessage(res.message || '');
            setResultStatus(res.status);

            if (res.status === 'WINNER_PENDING') {
                rouletteAudioEngine.playSuccessBeep();
            } else {
                rouletteAudioEngine.playErrorBeep();
            }
        } catch (err) {
            setResultStatus('NOT_FOUND');
            setResultMessage('Ocurrió un error al buscar el registro del aficionado.');
            rouletteAudioEngine.playErrorBeep();
        } finally {
            setIsLoadingLookup(false);
        }
    };

    // =========================================================================
    // LÓGICA DE EJECUCIÓN DE CANJE (REDEEM)
    // =========================================================================
    const handleExecuteRedeem = async ({
        passCode = '',
        redeemChannel = 'MANUAL_FOLIO',
        autoLookup = false
    } = {}) => {
        const targetCode = passCode || resultData?.passCode || manualInput;
        if (!targetCode) return;

        setIsRedeeming(true);

        try {
            // Si es canje rápido, primero aseguramos consultar si era ganador si no teníamos los datos
            if (autoLookup && !resultData) {
                const lookupRes = await LookupVoucherBL(roomId, targetCode);
                if (lookupRes.status === 'ALREADY_REDEEMED') {
                    setResultStatus('ALREADY_REDEEMED');
                    setResultData(lookupRes.data?.participant || lookupRes.data);
                    setResultMessage(lookupRes.message || 'Este premio ya había sido canjeado.');
                    rouletteAudioEngine.playErrorBeep();
                    setIsRedeeming(false);
                    return;
                } else if (lookupRes.status === 'NOT_A_WINNER') {
                    setResultStatus('NOT_A_WINNER');
                    setResultData(lookupRes.data?.participant || lookupRes.data);
                    setResultMessage(lookupRes.message || 'Pase de cortesía sin premio físico.');
                    rouletteAudioEngine.playErrorBeep();
                    setIsRedeeming(false);
                    return;
                } else if (lookupRes.status === 'NOT_FOUND') {
                    setResultStatus('NOT_FOUND');
                    setResultMessage('No se encontró ningún registro para el código escaneado.');
                    rouletteAudioEngine.playErrorBeep();
                    setIsRedeeming(false);
                    return;
                }
            }

            const res = await RedeemPrizeBL({
                roomId,
                passCode: targetCode,
                redeemChannel,
                operatorName,
                notes: notesInput
            });

            if (res.success) {
                rouletteAudioEngine.playSuccessBeep();
                setResultStatus('REDEEMED_SUCCESS');
                setResultMessage(res.message || '¡Premio entregado y canjeado exitosamente!');
                if (res.data?.participant) {
                    setResultData(res.data.participant);
                } else if (resultData) {
                    setResultData(prev => ({
                        ...prev,
                        hasRedeemedPrize: true,
                        redeemedAtUtc: new Date().toISOString(),
                        redeemedBy: operatorName,
                        redeemedChannel: redeemChannel
                    }));
                }

                if (typeof onRedeemSuccess === 'function') {
                    onRedeemSuccess(res.data);
                }
            } else {
                rouletteAudioEngine.playErrorBeep();
                if (res.status === 'ALREADY_REDEEMED') {
                    setResultStatus('ALREADY_REDEEMED');
                } else {
                    setResultStatus('ERROR');
                }
                setResultMessage(res.message || 'Error al completar el canje.');
            }
        } catch (err) {
            rouletteAudioEngine.playErrorBeep();
            setResultStatus('ERROR');
            setResultMessage('Error de comunicación con el servidor al registrar el canje.');
        } finally {
            setIsRedeeming(false);
        }
    };

    // =========================================================================
    // RESTABLECER Y ESCANEAR SIGUIENTE
    // =========================================================================
    const handleResetForNext = () => {
        setResultStatus(null);
        setResultData(null);
        setResultMessage('');
        setManualInput('');
        setNotesInput('');
        isProcessingScanRef.current = false;

        if (activeTab === 'scanner') {
            startCameraScanner();
        }
    };

    if (!isOpen) return null;

    const participant = resultData;
    const prizeTitle = participant?.prizeTitleSnapshot || participant?.prizeText || participant?.prize?.textEs || 'Premio Oficial';

    return (
        <>
            {/* Backdrop con desenfoque de fondo */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.78)',
                    zIndex: 110000,
                    backdropFilter: 'blur(5px)'
                }}
            />

            {/* Modal Principal Centrado y Responsivo */}
            <div
                className="PersonalRouletteRedeemModal"
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '95%',
                    maxWidth: '620px',
                    maxHeight: '92vh',
                    zIndex: 110001,
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <div className="card shadow-2xl border-0 rounded-4 overflow-hidden text-dark bg-white d-flex flex-column h-100">
                    {/* 1. ENCABEZADO DEL MODAL */}
                    <div className="bg-danger p-3 px-4 d-flex justify-content-between align-items-center text-white shadow-sm flex-shrink-0">
                        <div className="d-flex align-items-center gap-2">
                            <div className="rounded-circle bg-white text-danger d-flex align-items-center justify-content-center" style={{ width: '38px', height: '38px' }}>
                                <i className="bi bi-gift-fill fs-5"></i>
                            </div>
                            <div>
                                <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                    Canje y Entrega de Premios
                                </h5>
                                <small className="text-white-50">Ruleta Baja Cup • Validación en Sitio</small>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-dark bg-opacity-50 text-white border border-light border-opacity-25 px-2.5 py-1.5 small font-monospace">
                                <i className="bi bi-person-badge-fill me-1 text-warning"></i>
                                {operatorName}
                            </span>
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                onClick={onClose}
                                aria-label="Cerrar modal"
                            ></button>
                        </div>
                    </div>

                    {/* 2. PESTAÑAS DE SELECCIÓN DE MODO (Búsqueda manual activada) */}
                    <div className="bg-light border-bottom px-3 pt-2 flex-shrink-0">
                        <ul className="nav nav-tabs border-0 gap-1">
                            {/* Pestaña de escáner de cámara comentada temporalmente a petición del operador
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold d-flex align-items-center gap-2 px-3 py-2.5 border-0 ${
                                        activeTab === 'scanner' ? 'active bg-white text-danger border-bottom border-danger border-3' : 'text-secondary'
                                    }`}
                                    onClick={() => {
                                        setActiveTab('scanner');
                                        setResultStatus(null);
                                    }}
                                >
                                    <i className="bi bi-camera-fill"></i>
                                    <span>Escáner Cámara QR</span>
                                </button>
                            </li>
                            */}
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className="nav-link fw-bold d-flex align-items-center gap-2 px-3 py-2.5 border-0 active bg-white text-danger border-bottom border-danger border-3"
                                    onClick={() => {
                                        setActiveTab('manual');
                                        stopCameraScanner();
                                    }}
                                >
                                    <i className="bi bi-keyboard-fill"></i>
                                    <span>Búsqueda Manual</span>
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* 3. CONTENIDO PRINCIPAL SCROLLEABLE */}
                    <div className="card-body p-3 p-md-4 overflow-auto flex-grow-1" style={{ maxHeight: 'calc(92vh - 140px)' }}>
                        {/* TAB 1: ESCÁNER DE CÁMARA QR (Comentado temporalmente)
                        {activeTab === 'scanner' && !resultStatus && (
                            <div className="PersonalRouletteRedeemModal__ScannerView">
                                ...
                            </div>
                        )}
                        */}

                        {/* ===================================================== */}
                        {/* TAB 2: BÚSQUEDA MANUAL */}
                        {/* ===================================================== */}
                        {activeTab === 'manual' && !resultStatus && (
                            <div className="PersonalRouletteRedeemModal__ManualView">
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        handleLookupParticipant(manualInput, 'MANUAL_FOLIO');
                                    }}
                                >
                                    <div className="mb-3">
                                        <label className="form-label fw-bold small text-dark d-flex align-items-center gap-1">
                                            <i className="bi bi-search text-danger"></i>
                                            <span>Folio, Teléfono a 10 dígitos o Correo Electrónico:</span>
                                        </label>
                                        <div className="input-group input-group-lg shadow-sm">
                                            <span className="input-group-text bg-white border-end-0 text-muted">
                                                <i className="bi bi-upc-scan fs-5"></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control border-start-0 font-monospace text-uppercase"
                                                placeholder="Ej. #SD26-XXXX, 6195550199..."
                                                value={manualInput}
                                                onChange={(e) => setManualInput(e.target.value)}
                                                autoFocus
                                            />
                                            {manualInput && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary border-start-0"
                                                    onClick={() => setManualInput('')}
                                                >
                                                    <i className="bi bi-x-lg"></i>
                                                </button>
                                            )}
                                        </div>
                                        <div className="form-text text-muted small mt-1.5">
                                            Puedes ingresar el folio completo con o sin hashtag (#), número celular registrado o correo.
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        className="btn btn-danger btn-lg w-100 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2 py-3"
                                        disabled={isLoadingLookup || !manualInput.trim()}
                                    >
                                        {isLoadingLookup ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                                <span>Buscando y Validando...</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-shield-check fs-5"></i>
                                                <span>Buscar y Validar Pase</span>
                                            </>
                                        )}
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* ===================================================== */}
                        {/* PANEL DE RESULTADOS / PRE-CHECK CARD */}
                        {/* ===================================================== */}
                        {resultStatus && (
                            <div className="PersonalRouletteRedeemModal__ResultView animate__animated animate__fadeIn">
                                {/* CASO 1: GANADOR PENDIENTE DE CANJE (WINNER_PENDING) */}
                                {resultStatus === 'WINNER_PENDING' && (
                                    <div className="card border-success border-2 rounded-4 p-3 p-md-4 bg-success-subtle shadow-sm mb-3">
                                        <div className="d-flex justify-content-between align-items-start mb-3 border-bottom border-success border-opacity-25 pb-2">
                                            <div>
                                                <span className="badge bg-success px-3 py-1.5 fs-6 fw-bold text-uppercase d-inline-flex align-items-center gap-1.5 mb-1">
                                                    <i className="bi bi-check-circle-fill"></i>
                                                    ★ Ganador Verificado • Listo para Entregar
                                                </span>
                                                <h4 className="fw-bold text-dark mb-0 mt-1">
                                                    {participant?.fullName || 'Aficionado Ganador'}
                                                </h4>
                                            </div>
                                            <span className="badge bg-dark font-monospace fs-5 px-3 py-2 shadow-sm">
                                                {participant?.passCode || participant?.folio || manualInput}
                                            </span>
                                        </div>

                                        {/* Tarjeta del Premio Ganado */}
                                        <div className="p-3 bg-white rounded-3 border border-success border-opacity-25 shadow-sm mb-3">
                                            <div className="d-flex align-items-center gap-3">
                                                <div
                                                    className="rounded-3 d-flex align-items-center justify-content-center text-white shadow-sm flex-shrink-0"
                                                    style={{ width: '56px', height: '56px', backgroundColor: participant?.prize?.backgroundColor || '#dc2626' }}
                                                >
                                                    <i className="bi bi-trophy-fill fs-3 text-warning"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="text-muted small fw-semibold text-uppercase">Premio Obtenido:</div>
                                                    <h5 className="fw-bold text-dark mb-0">{prizeTitle}</h5>
                                                    {participant?.prize?.descriptionEs && (
                                                        <small className="text-secondary">{participant.prize.descriptionEs}</small>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Metadatos del Participante */}
                                        <div className="row g-2 small text-secondary mb-3">
                                            <div className="col-12 col-sm-6">
                                                <i className="bi bi-telephone me-1 text-dark"></i>
                                                <strong>Tel:</strong> {participant?.phone || 'N/A'}
                                            </div>
                                            <div className="col-12 col-sm-6">
                                                <i className="bi bi-envelope me-1 text-dark"></i>
                                                <strong>Email:</strong> {participant?.email || 'N/A'}
                                            </div>
                                            <div className="col-12 col-sm-6">
                                                <i className="bi bi-calendar-check me-1 text-dark"></i>
                                                <strong>Giro realizado:</strong> {formatEventDate(participant?.playedAt || participant?.createdAtUtc)}
                                            </div>
                                            <div className="col-12 col-sm-6">
                                                <i className="bi bi-geo-alt me-1 text-dark"></i>
                                                <strong>ZIP:</strong> {participant?.zipCode || 'N/A'}
                                            </div>
                                        </div>

                                        {/* Campo opcional de notas para el staff */}
                                        <div className="mb-3">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm bg-white"
                                                placeholder="Nota opcional del operador (ej. Talla M, entregado en stand norte)..."
                                                value={notesInput}
                                                onChange={(e) => setNotesInput(e.target.value)}
                                            />
                                        </div>

                                        {/* Botón de Acción Principal de Canje */}
                                        <button
                                            type="button"
                                            className="btn btn-success btn-lg w-100 py-3 fw-bold shadow d-flex align-items-center justify-content-center gap-2 text-uppercase"
                                            onClick={() => handleExecuteRedeem({
                                                passCode: participant?.passCode || manualInput,
                                                redeemChannel: activeTab === 'scanner' ? 'QR_SCAN' : 'MANUAL_FOLIO'
                                            })}
                                            disabled={isRedeeming}
                                        >
                                            {isRedeeming ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                                    <span>Registrando Entrega...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-gift-fill fs-5"></i>
                                                    <span>★ Confirmar y Entregar Premio</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}

                                {/* CASO 2: PREMIO YA CANJEADO (ALREADY_REDEEMED) */}
                                {resultStatus === 'ALREADY_REDEEMED' && (
                                    <div className="card border-danger border-2 rounded-4 p-3 p-md-4 bg-danger-subtle shadow-sm mb-3">
                                        <div className="d-flex align-items-center gap-2 mb-2 text-danger">
                                            <i className="bi bi-exclamation-octagon-fill fs-3"></i>
                                            <h5 className="fw-bold mb-0 text-danger">
                                                ⚠️ ¡ATENCIÓN! ESTE PREMIO YA FUE CANJEADO
                                            </h5>
                                        </div>
                                        <p className="text-danger-emphasis small mb-3">
                                            El folio presentado ya figura como entregado en el sistema y no puede ser redimido por segunda vez.
                                        </p>

                                        <div className="p-3 bg-white rounded-3 border border-danger border-opacity-25 shadow-sm mb-3">
                                            <div className="row g-2 small">
                                                <div className="col-12 col-sm-6">
                                                    <strong>Folio:</strong> <span className="font-monospace text-danger fw-bold">{participant?.passCode || manualInput}</span>
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <strong>Ganador:</strong> {participant?.fullName || 'N/A'}
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <strong>Premio:</strong> {prizeTitle}
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <strong>Fecha de Canje:</strong> {formatEventDate(participant?.redeemedAtUtc || participant?.redeemedAt)}
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <strong>Operador que Entregó:</strong> <span className="badge bg-secondary">{participant?.redeemedBy || 'Staff'}</span>
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <strong>Canal de Canje:</strong> <span className="badge bg-dark font-monospace">{participant?.redeemedChannel || 'MANUAL_FOLIO'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* CASO 3: PASE DE CORTESÍA / NO GANADOR (NOT_A_WINNER) */}
                                {resultStatus === 'NOT_A_WINNER' && (
                                    <div className="card border-warning border-2 rounded-4 p-3 p-md-4 bg-warning-subtle shadow-sm mb-3">
                                        <div className="d-flex align-items-center gap-2 mb-2 text-warning-emphasis">
                                            <i className="bi bi-info-circle-fill fs-3 text-warning"></i>
                                            <h5 className="fw-bold mb-0 text-dark">
                                                Pase de Cortesía (Sin Premio Físico)
                                            </h5>
                                        </div>
                                        <p className="text-secondary small mb-3">
                                            Este aficionado participó en la ruleta y obtuvo la casilla de cortesía o agradecimiento. No cuenta con un premio físico asignado para entregar.
                                        </p>
                                        <div className="p-3 bg-white rounded-3 border shadow-sm small text-secondary">
                                            <div><strong>Folio:</strong> <span className="font-monospace text-dark fw-bold">{participant?.passCode || manualInput}</span></div>
                                            <div><strong>Participante:</strong> {participant?.fullName || 'N/A'}</div>
                                            <div><strong>Fecha de participación:</strong> {formatEventDate(participant?.playedAt || participant?.createdAtUtc)}</div>
                                        </div>
                                    </div>
                                )}

                                {/* CASO 4: NO ENCONTRADO (NOT_FOUND) */}
                                {resultStatus === 'NOT_FOUND' && (
                                    <div className="card border-secondary border-opacity-50 rounded-4 p-3 p-md-4 bg-light shadow-sm mb-3 text-center">
                                        <i className="bi bi-search fs-1 text-muted mb-2"></i>
                                        <h5 className="fw-bold text-dark mb-1">No se encontró ningún registro</h5>
                                        <p className="text-secondary small mb-3">
                                            {resultMessage || 'No se encontró ningún aficionado registrado con el folio, QR o teléfono ingresado. Verifica los datos e intenta de nuevo.'}
                                        </p>
                                        <span className="badge bg-white text-dark border font-monospace fs-6 py-2 px-3 align-self-center">
                                            Búsqueda: {manualInput || 'Código QR'}
                                        </span>
                                    </div>
                                )}

                                {/* CASO 5: CANJE EXITOSO (REDEEMED_SUCCESS) */}
                                {resultStatus === 'REDEEMED_SUCCESS' && (
                                    <div className="card border-success border-2 rounded-4 p-3 p-md-4 bg-success text-white shadow-lg mb-3 text-center animate__animated animate__zoomIn">
                                        <div className="rounded-circle bg-white text-success d-inline-flex align-items-center justify-content-center mx-auto mb-2 shadow" style={{ width: '64px', height: '64px' }}>
                                            <i className="bi bi-check-lg fs-1 fw-bold"></i>
                                        </div>
                                        <h3 className="fw-bold mb-1">¡Premio Canjeado con Éxito!</h3>
                                        <p className="text-white-50 small mb-3">
                                            La entrega física del premio ha quedado registrada permanentemente en la base de datos.
                                        </p>

                                        <div className="p-3 bg-white text-dark rounded-3 shadow-sm text-start small mb-1">
                                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                                                <span className="fw-bold text-success fs-6">★ {prizeTitle}</span>
                                                <span className="badge bg-dark font-monospace">{participant?.passCode || manualInput}</span>
                                            </div>
                                            <div className="text-secondary">
                                                <strong>Entregado a:</strong> {participant?.fullName || 'Aficionado'}<br />
                                                <strong>Operador responsable:</strong> {operatorName}<br />
                                                <strong>Hora de entrega:</strong> {new Date().toLocaleTimeString('es-MX', { hour12: false })}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* CASO 6: ERROR GENERAL */}
                                {resultStatus === 'ERROR' && (
                                    <div className="card border-danger border-2 rounded-4 p-3 p-md-4 bg-danger-subtle shadow-sm mb-3">
                                        <div className="d-flex align-items-center gap-2 text-danger mb-2">
                                            <i className="bi bi-x-circle-fill fs-3"></i>
                                            <h5 className="fw-bold mb-0">Error en el Proceso de Canje</h5>
                                        </div>
                                        <p className="text-danger-emphasis small mb-0">
                                            {resultMessage || 'Ocurrió un error inesperado al intentar canjear el premio.'}
                                        </p>
                                    </div>
                                )}

                                {/* BOTÓN DE RESTABLECER / SIGUIENTE (1 CLIC) */}
                                <button
                                    type="button"
                                    className="btn btn-dark btn-lg w-100 py-2.5 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
                                    onClick={handleResetForNext}
                                >
                                    <i className="bi bi-arrow-repeat fs-5"></i>
                                    <span>🔄 Escanear Siguiente / Nueva Búsqueda</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* 4. PIE DE MODAL */}
                    <div className="card-footer bg-light p-3 px-4 d-flex justify-content-between align-items-center border-top flex-shrink-0">
                        <small className="text-muted">
                            <i className="bi bi-shield-lock me-1"></i>
                            Módulo de entrega auditado en tiempo real
                        </small>
                        <button
                            type="button"
                            className="btn btn-outline-secondary px-3"
                            onClick={onClose}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PersonalRouletteRedeemModal;
